import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Collapse,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import StarIcon from '@mui/icons-material/Star';
import { useSuperDuper } from '../context/SuperDuperContext';
import { renderLoading, renderError } from '../components/StatusViews';
import PublicFolderImageField from '../PublicFolderImageField';

const QuizCreatorTab = () => {
  const {
    quizForm,
    isCreatingQuiz,
    quizzes,
    quizzesLoading,
    quizzesError,
    editingQuizId,
    showQuizCreator,
    setShowQuizCreator,
    handleQuizFormChange,
    handleQuestionChange,
    handleOptionChange,
    handleAddQuestion,
    handleRemoveQuestion,
    handleAddImage,
    handleImageChange,
    handleRemoveImage,
    handleCreateQuiz,
    handleEditQuiz,
    handleCancelEdit,
    handleDeleteQuiz
  } = useSuperDuper();

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
          mb: 3
        }}
      >
        <Typography variant="h4" sx={{ fontSize: { xs: '1.35rem', sm: '2.125rem' } }}>
          Quiz Creator
        </Typography>
        {!showQuizCreator && !editingQuizId && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowQuizCreator(true)}
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Create New Quiz
          </Button>
        )}
        {editingQuizId && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancelEdit}
            sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}
          >
            Cancel Edit
          </Button>
        )}
      </Box>

      <Collapse in={showQuizCreator || editingQuizId}>
        <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Quiz Information
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Quiz Name *"
                value={quizForm.name}
                onChange={handleQuizFormChange('name')}
                placeholder="Enter quiz name"
                helperText="Required: e.g., 'Junior Quiz', 'Senior Quiz'"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <PublicFolderImageField
                label="Quiz Banner URL"
                value={quizForm.banner}
                onChange={(url) => handleQuizFormChange('banner')({ target: { value: url } })}
                placeholder="Enter banner image URL (optional)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={quizForm.isActive}
                    onChange={handleQuizFormChange('isActive')}
                    color="primary"
                  />
                }
                label="Quiz is Active"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={quizForm.isFavourite}
                    onChange={handleQuizFormChange('isFavourite')}
                    color="secondary"
                  />
                }
                label="Mark as Favourite (Max 3)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Questions Section */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Questions ({quizForm.questions.length})
      </Typography>

      {quizForm.questions.map((question, qIndex) => (
        <Card key={qIndex} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Question {qIndex + 1}
              </Typography>
              {quizForm.questions.length > 1 && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleRemoveQuestion(qIndex)}
                >
                  Remove
                </Button>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Question Text *"
                  value={question.question}
                  onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                  placeholder="Enter the question"
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Options */}
              {question.options.map((option, oIndex) => (
                <Grid item xs={12} sm={6} key={oIndex}>
                  <TextField
                    fullWidth
                    label={`Option ${oIndex + 1} *`}
                    value={option}
                    onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                    placeholder={`Enter option ${oIndex + 1}`}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Correct Answer *"
                  value={question.correctAnswer}
                  onChange={(e) => handleQuestionChange(qIndex, 'correctAnswer', parseInt(e.target.value))}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {question.options.map((opt, idx) => (
                    <option key={idx} value={idx}>
                      Option {idx + 1}: {opt || '(empty)'}
                    </option>
                  ))}
                </TextField>
              </Grid>

              {/* Images Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Images (Optional)
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => handleAddImage(qIndex)}
                  >
                    Add Image
                  </Button>
                </Box>

                {question.images && question.images.length > 0 && (
                  <Grid container spacing={2}>
                    {question.images.map((img, imgIndex) => (
                      <Grid item xs={12} key={imgIndex}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <PublicFolderImageField
                              label={`Image URL ${imgIndex + 1}`}
                              value={img}
                              onChange={(url) => handleImageChange(qIndex, imgIndex, url)}
                              placeholder="Enter image URL"
                            />
                          </Box>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() => handleRemoveImage(qIndex, imgIndex)}
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon />
                          </Button>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* Add Question Button */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={handleAddQuestion}
        >
          Add Another Question
        </Button>
      </Box>

      {/* Create/Update Quiz Button */}
      <Box sx={{ textAlign: 'center', mb: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleCreateQuiz}
          disabled={isCreatingQuiz || !quizForm.name}
          startIcon={isCreatingQuiz ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ minWidth: 200 }}
        >
          {isCreatingQuiz 
            ? (editingQuizId ? 'Updating Quiz...' : 'Creating Quiz...') 
            : (editingQuizId ? 'Update Quiz' : 'Create Quiz')
          }
        </Button>
        {editingQuizId && (
          <Button
            variant="outlined"
            color="secondary"
            size="large"
            onClick={handleCancelEdit}
            sx={{ minWidth: 150 }}
          >
            Cancel
          </Button>
        )}
      </Box>
      </Collapse>

      {/* Existing Quizzes List */}
      <Divider sx={{ my: 4 }} />
      <Typography variant="h5" sx={{ mb: 3 }}>
        Existing Quizzes
      </Typography>

      {quizzesLoading ? (
        renderLoading()
      ) : quizzesError ? (
        renderError(quizzesError)
      ) : (
        <Box>
          {quizzes.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="textSecondary">
                No quizzes created yet. Create your first quiz above!
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {quizzes.map((quiz) => (
                <Paper 
                  key={quiz._id}
                  sx={{ 
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    '&:hover': {
                      boxShadow: 3
                    }
                  }}
                >
                  {/* Quiz Image or Placeholder */}
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      flexShrink: 0,
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'grey.200',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {quiz.banner ? (
                      <img 
                        src={quiz.banner} 
                        alt={quiz.name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover' 
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div style="color: #999; font-size: 2rem;">📝</div>';
                        }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '2rem' }}>📝</Typography>
                    )}
                  </Box>

                  {/* Quiz Details */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      {quiz.isFavourite && (
                        <StarIcon sx={{ color: '#FFD700', fontSize: '1.2rem' }} />
                      )}
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {quiz.name}
                      </Typography>
                      <Chip
                        label={quiz.isActive ? 'Active' : 'Inactive'}
                        color={quiz.isActive ? 'success' : 'default'}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="textSecondary">
                        📊 {quiz.questions?.length || 0} Questions
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        📅 {new Date(quiz.createdAt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })} at {new Date(quiz.createdAt).toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditQuiz(quiz)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteQuiz(quiz)}
                    >
                      Delete
                    </Button>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default QuizCreatorTab;
